// =============================================================================
// ui/state/usePropUniverse.ts — the whole book, scoped so Class L can use it.
// -----------------------------------------------------------------------------
// The one place in `ui/` that mints a `WholeBook`, and the reason the Prop Report
// was blocked until now: `asWholeBook` refuses anything without a receipt proving
// the read covered everything, and no read in this client produced one.
//
// ── A HOOK, NOT A PROVIDER ──
//
// Every other catalogue (patterns, exit models, contracts) loads at the shell
// because several tabs need it. This is the whole trade history and exactly one
// surface needs it, so a provider would make every user pay the read to open the
// Dashboard. It fires when the Prop Report mounts.
//
// ── ⚠ WHY THE POPULATION IS BUILT IN THE EFFECT, NOT IN A MEMO ──
//
// `asWholeBook` THROWS when the receipt does not justify a whole book, and that
// is the whole design — but a throw inside render is a blank screen, and a blank
// screen is indistinguishable from a bug. `coversWindow` exists in
// `selectors/population.ts` for the same reason on the Class-W side.
//
// So the refusal is caught here and becomes `phase: 'failed'` with the thrown
// message, which the tab renders as text. The guard still fires; it just reaches
// the user as a sentence instead of a white page.
// =============================================================================

import { useEffect, useState } from 'react';
import { useSupabase } from './supabase';
import { loadPropUniverse, type PropRow } from '../../io/sync/propUniverse';
import { asWholeBook, type WholeBook } from '../../selectors/population';
import { reportFailure } from '../../io/diagnostics/errorLog';

export type PropUniversePhase = 'loading' | 'ready' | 'failed';

export interface PropUniverseResult {
  phase: PropUniversePhase;
  /**
   * ⚠ NULL until the read has succeeded AND been proven whole. A consumer that
   * treats null as "empty book" would render a fully-funded account at zero
   * drawdown used — the optimistic direction again.
   */
  population: WholeBook<PropRow> | null;
  error: string | null;
  reload: () => void;
}

export function usePropUniverse(): PropUniverseResult {
  const { sb } = useSupabase();
  const [phase, setPhase] = useState<PropUniversePhase>('loading');
  const [population, setPopulation] = useState<WholeBook<PropRow> | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [nonce, setNonce] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setPhase('loading');
    setError(null);

    void (async () => {
      try {
        const { rows, receipt } = await loadPropUniverse(sb);
        // ⚠ Inside the try DELIBERATELY. This is the call that can refuse, and
        // routing its refusal through the same channel as a network failure is
        // correct: from the user's side both mean "this screen cannot be
        // trusted, so it is not being drawn".
        const whole = asWholeBook(rows, receipt);
        if (cancelled) return;
        setPopulation(whole);
        setPhase('ready');
      } catch (e) {
        if (cancelled) return;
        // ⚠ `population` keeps whatever it had — never an empty whole book. A
        // zero-row WholeBook is the most dangerous value in this module: it is
        // structurally valid, so every Class-L number computes cleanly and every
        // one of them describes an account that has never traded.
        setError(reportFailure('prop accounts', e));
        setPhase('failed');
      }
    })();

    return () => { cancelled = true; };
  }, [sb, nonce]);

  return { phase, population, error, reload: () => setNonce((n) => n + 1) };
}
